import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Package } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { mockData } from '../data/mockData';
import EditableText from './EditableText';

const ProjectsSection = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [expandedProject, setExpandedProject] = useState(null);
  const { t } = useTranslation();

  const categories = [
    { id: 'all', name: t('projects.allProjects') },
    { id: 'electronics', name: t('projects.categories.electronics') },
    { id: 'machinery', name: t('projects.categories.machinery') },
    { id: 'tools', name: t('projects.categories.tools') },
    { id: 'packaging', name: t('projects.categories.packaging') },
    { id: 'consumer', name: t('projects.categories.consumer') },
    { id: 'textiles', name: t('projects.categories.textiles') },
    { id: 'apparel', name: t('projects.categories.apparel') }
  ];

  // Function to get translated products for a category
  const getTranslatedProducts = (category) => {
    const productMap = {
      electronics: ['solarPanels', 'electricBikes', 'ledLights', 'powerBanks', 'smartHome'],
      machinery: ['cncMachining', 'conveyor', 'hydraulicPress', 'pneumaticTools', 'weldingEquipment'],
      tools: ['measuring', 'toolSets', 'powerAccessories', 'handTools', 'safetyEquipment'],
      packaging: ['packagingMachines', 'labelingSystems', 'sealingEquipment', 'corrugatedBoxes', 'protectivePackaging'],
      consumer: ['smartAppliances', 'furniture', 'kitchenGadgets', 'fitnessAccessories', 'phoneAccessories'],
      textiles: ['technicalFabrics', 'sustainableClothing', 'homeTextiles', 'workwear', 'cottonProducts'],
      apparel: ['babyProducts', 'kidsClothing', 'adultsFashion', 'maternitywear', 'seasonalCollections', 'footwearAccessories']
    };

    const productKeys = productMap[category] || [];
    return productKeys.map(key => ({
      name: t(`products.${category}.${key}.name`),
      description: t(`products.${category}.${key}.description`)
    }));
  };

  // Create projects with translated product data
  const translatedProjects = mockData.projects.map(project => ({
    ...project,
    title: t(`projects.categories.${project.category}`),
    products: getTranslatedProducts(project.category)
  }));

  const filteredProjects = activeCategory === 'all' 
    ? translatedProjects 
    : translatedProjects.filter(project => project.category === activeCategory);

  const toggleExpansion = (projectId) => {
    setExpandedProject(expandedProject === projectId ? null : projectId);
  };

  return (
    <section id="projects" className="py-20 lg:py-28 bg-white">
      <div className="container mx-auto px-6 max-w-7xl">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16">
          <EditableText
            section="projects"
            field="subtitle"
            as="p"
            className="text-blue-600 text-base sm:text-lg font-medium mb-4"
          >
            {t('projects.subtitle')}
          </EditableText>
          <EditableText
            section="projects"
            field="title"
            as="h2"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6 sm:mb-8"
          >
            {t('projects.title')}
          </EditableText>
          <EditableText
            section="projects"
            field="description"
            as="p"
            className="text-lg sm:text-xl text-slate-600 max-w-3xl mx-auto px-4"
          >
            {t('projects.description')}
          </EditableText>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-8 sm:mb-12 px-4">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full font-semibold transition-all duration-300 text-sm sm:text-base ${
                activeCategory === category.id
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
              data-testid={`product-filter-${category.id}`}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-8 sm:mb-12 px-4 sm:px-0">
          {filteredProjects.map((project) => (
            <div
              key={project.id}
              className="group relative bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg hover:border-slate-300 transition-all duration-300"
              data-testid={`product-card-${project.id}`}
            >
              {/* Image */}
              <div className="relative h-48 sm:h-56 overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-slate-900 bg-opacity-0 group-hover:bg-opacity-60 transition-all duration-500 flex items-center justify-center">
                  <div className="text-white text-center transform translate-y-8 group-hover:translate-y-0 transition-transform duration-500 opacity-0 group-hover:opacity-100">
                    <h3 className="text-xl sm:text-2xl font-bold mb-2">{project.title}</h3>
                    <div className="flex items-center justify-center space-x-2 text-sm">
                      <Package className="w-4 h-4" />
                      <span>{t('projects.viewDetails')}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-4 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <EditableText
                    section="projects"
                    field={`${project.category}_title`}
                    as="h3"
                    className="text-lg sm:text-xl font-bold text-slate-900 flex-1"
                  >
                    {project.title}
                  </EditableText>
                  <button 
                    onClick={() => toggleExpansion(project.id)}
                    className="flex items-center text-blue-700 hover:text-blue-900 font-semibold transition-colors duration-300 ml-4 text-sm"
                  >
                    {expandedProject === project.id ? (
                      <>
                        <EditableText section="projects" field="hideButton" as="span" className="mr-1">
                          {t('ui.hide')}
                        </EditableText>
                        <ChevronUp className="w-4 h-4" />
                      </>
                    ) : (
                      <>
                        <EditableText section="projects" field="viewProductsButton" as="span" className="mr-1">
                          {t('projects.viewProducts')}
                        </EditableText>
                        <ChevronDown className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>

                {/* Expanded Product List */}
                {expandedProject === project.id && (
                  <div className="border-t border-slate-200 pt-4 space-y-3 animate-in slide-in-from-top duration-300">
                    {project.products.map((product, index) => (
                      <div key={index} className="border border-slate-100 rounded-lg p-3 sm:p-4 hover:shadow-sm hover:border-slate-200 transition-all duration-200">
                        <EditableText
                          section="projects"
                          field={`${project.category}_product${index + 1}_name`}
                          as="h4"
                          className="font-semibold text-slate-900 text-sm sm:text-base mb-2"
                        >
                          {product.name}
                        </EditableText>
                        <EditableText
                          section="projects"
                          field={`${project.category}_product${index + 1}_description`}
                          as="p"
                          className="text-gray-600 text-xs sm:text-sm leading-relaxed"
                        >
                          {product.description}
                        </EditableText>
                      </div>
                    ))}
                    
                    {/* Contact Information */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 mt-4">
                      <EditableText
                        section="projects"
                        field="contactForDetails"
                        as="p"
                        className="text-xs sm:text-sm text-slate-700 text-center"
                      >
                        {t('projects.contactForDetails')}
                      </EditableText>
                      <p className="text-xs sm:text-sm text-blue-700 text-center mt-2">
                        <button 
                          onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                          className="font-semibold underline hover:text-blue-900 transition-colors duration-200"
                        >
                          <EditableText
                            section="projects"
                            field="contactFormLink"
                            as="span"
                          >
                            {t('contact.contactForm')}
                          </EditableText>
                        </button>
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center px-4 sm:px-0">
          <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-2xl p-6 sm:p-8 mb-8 border border-slate-200">
            <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4">{t('projects.lookingForSpecific')}</h3>
            <p className="text-slate-600 mb-6 max-w-2xl mx-auto text-sm sm:text-base">
              {t('projects.lookingDescription')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 sm:px-8 py-3 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg text-sm sm:text-base"
              >
                {t('projects.contactTeam')}
              </button>
              <button 
                onClick={() => {
                  setActiveCategory('all');
                  document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="border-2 border-slate-800 text-slate-800 hover:bg-slate-800 hover:text-white px-6 sm:px-8 py-3 rounded-lg font-semibold transition-all duration-300 text-sm sm:text-base"
              >
                {t('projects.viewAllCategories')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectsSection;